/**
 * /hospitality/lodging — Tower occupancy, ADR and RevPAR (P3 lodging sub-pack).
 *
 * Queries GET /api/hospitality/lodging/performance. One stat card per tower
 * (Conrad, Crockfords, Hilton LV) plus a property-wide summary row.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/lodging/")({
  component: LodgingPerformancePage,
});

interface TowerRow {
  code: string;
  name: string;
  total_rooms: number;
  rooms_sold: number;
  rooms_ooo: number;
  comp_rooms: number;
  available_rooms: number;
  occupancy_pct: string;
  adr: string;
  revpar: string;
  room_revenue: string;
}
interface LodgingTotals {
  total_rooms: number;
  rooms_sold: number;
  rooms_ooo: number;
  available_rooms: number;
  occupancy_pct: string;
  adr: string;
  revpar: string;
  room_revenue: string;
}
interface LodgingPerformanceResult {
  property: string;
  business_date: string | null;
  by_tower: TowerRow[];
  totals: LodgingTotals | null;
  message?: string;
}

function LodgingPerformancePage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-lodging-performance"],
    queryFn: () => api<LodgingPerformanceResult>("/api/hospitality/lodging/performance"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Rooms Performance"
        subtitle={
          data
            ? `${data.property}${data.business_date ? ` · ${data.business_date}` : ""}`
            : "Tower occupancy, ADR and RevPAR"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Property summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard
                label="Occupancy"
                value={data.totals?.occupancy_pct ?? "—"}
              />
              <StatCard
                label="ADR"
                value={money(data.totals?.adr ?? null)}
              />
              <StatCard
                label="RevPAR"
                value={money(data.totals?.revpar ?? null)}
              />
              <StatCard
                label="Room revenue"
                value={money(data.totals?.room_revenue ?? null, 0)}
              />
            </div>

            {/* By tower */}
            <Panel label="By tower">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Tower</th>
                    <th className="text-right py-1.5 px-3">Total rms</th>
                    <th className="text-right py-1.5 px-3">Sold</th>
                    <th className="text-right py-1.5 px-3">OOO</th>
                    <th className="text-right py-1.5 px-3">Comps</th>
                    <th className="text-right py-1.5 px-3">Occupancy</th>
                    <th className="text-right py-1.5 px-3">ADR</th>
                    <th className="text-right py-1.5 px-3">RevPAR</th>
                    <th className="text-right py-1.5 pl-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_tower.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-500 dark:text-slate-400">
                        No lodging data for this business date.
                      </td>
                    </tr>
                  )}
                  {data.by_tower.map((t) => (
                    <tr key={t.code} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">{t.name}</div>
                        <div className="text-xs text-slate-400">{t.code}</div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {t.total_rooms.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {t.rooms_sold.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500 dark:text-slate-400">
                        {t.rooms_ooo}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500 dark:text-slate-400">
                        {t.comp_rooms}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {t.occupancy_pct}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(t.adr)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(t.revpar)}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(t.room_revenue, 0)}
                      </td>
                    </tr>
                  ))}
                  {data.totals && (
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-semibold">
                      <td className="py-1.5 pr-3 text-xs text-slate-700 dark:text-slate-200">Total</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {data.totals.total_rooms.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {data.totals.rooms_sold.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500">
                        {data.totals.rooms_ooo}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500">—</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-900 dark:text-slate-100">
                        {data.totals.occupancy_pct}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(data.totals.adr)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(data.totals.revpar)}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs text-slate-900 dark:text-slate-100">
                        {money(data.totals.room_revenue, 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
